import { useContext } from 'react'

import type { UseFormOptions } from '@domain-group/react-hooks'
import { useForm, useLatestEntity } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import PageContext from '@contexts/pageContext'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as adCopyActions from '@redux/modules/adCopy'
import type { AppDispatch } from '@redux/store'

import defaultFormState, { requiredFields } from '@models/adCopy'

import type { AdCopyModel, AdCopyRequestOptions } from '@models/types'

type AssignAdCopiesToCampaignParams = {
  ad_copy_ids: number[],
  campaign_id: number,
  dispatch: AppDispatch,
  entityOptions?: AdCopyRequestOptions,
}

const assignAdCopiesToCampaign = (params: AssignAdCopiesToCampaignParams) => {
  const {
    ad_copy_ids, campaign_id, dispatch, entityOptions,
  } = params

  const { assignToCampaign: assignFn } = adCopyActions

  return dispatch(assignFn(ad_copy_ids, campaign_id, entityOptions))
}

type CreateOrUpdateAdCopyParams = {
  adCopy: AdCopyModel,
  adCopyParams: Partial<AdCopyModel>,
  dispatch: AppDispatch,
  requestOptions?: AdCopyRequestOptions,
}

const createOrUpdateAdCopy = (params: CreateOrUpdateAdCopyParams) => {
  const {
    adCopy, adCopyParams, dispatch, requestOptions,
  } = params
  const { createOrUpdateAdCopy: updateOrCreateFn } = adCopyActions

  const updatedParams = {
    id: adCopy.id,
    ...adCopyParams,
  }

  return dispatch(updateOrCreateFn(updatedParams, requestOptions))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useAdCopyForm(
  adCopy: Partial<AdCopyModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const adCopyForm = useForm(
    defaultFormState,
    { entity: adCopy, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [adCopy.id, adCopy.cache_key],
  )

  return {
    ...adCopyForm,
  }
}

function useAdCopy(initEntity: Partial<AdCopyModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: adCopy }: { entity: AdCopyModel} = useLatestEntity(initEntity, 'adCopies')

  const dispatch = useDispatch()

  const {
    loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.adCopies)

  const { callbacks } = useContext(PageContext)

  return {
    adCopy,
    callbacks: {
      assignAdCopiesToCampaign: ({ campaign_id, ad_copy_ids, requestOptions }
        : {campaign_id: number, ad_copy_ids: number[], requestOptions?: AdCopyRequestOptions}) => (
        assignAdCopiesToCampaign({
          campaign_id, ad_copy_ids, entityOptions: requestOptions, dispatch,
        })
      ),
      createOrUpdateAdCopy: (adCopyParams: Partial<AdCopyModel>, entityOptions?: AdCopyRequestOptions) => (
        createOrUpdateAdCopy({
          adCopy, adCopyParams, dispatch, requestOptions: entityOptions,
        })
      ),
      reviewAdCopyRequests: (campaignId: number) => launchModal({
        callbacks,
        modalKey: 'AdCopyReviewRequestsModal',
        payload: { adCopy, campaignId },
      }),
    },
    loading: loading && !loadedIds.includes(adCopy.id),
    updating,
  }
}

export type UseAdCopyPayload = ReturnType<typeof useAdCopy>

export default useAdCopy
